import { ComparisonTable, Toast } from '@insurancemenu/imenu-components-lib/dist/components';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import DynamicFormModalDEPRECATED from 'components/common/modals/DynamicFormModal_DEPRECATED';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useRootStore } from 'store/rootStore';
import { ComparisonPageType } from 'store/ui-stores/comparison/comparisonStore';
import { useQueryParams } from '@insurancemenu/imenu-components-lib';
import { createStyles } from './styles';

export interface ComparisonContentProps {
  type: ComparisonPageType;
}

const ConfigComparisonContent = observer(({ type }: ComparisonContentProps): JSX.Element => {
  const classes = createStyles();
  const query = useQueryParams();

  const cartId = query.get('cartId') || '';
  const productLineId = query.get('productLineId') || '';
  const portfolioId = query.get('portfolioId') || '';
  const portfolioIds = query.get('portfolioIds') || '';
  const productIds = query.get('productIds') || '';

  const {
    comparisonStore: {
      loading,
      filteredPortfolios,
      cart,
      marketingTags,
      productLineAttrs,
      loaderInPortfolioId,
      toastInfo: { showToast, toastType, toastMessage },
      currentConfiguredPortfolioData,
      collectInitialData,
      resetConfiguredPortfolio,
      setConfiguredPortfolio,
      closeToast,
      onAddToProposal,
      findMatchingVersionIndex,
      onRemoveFromProposal,
    },
    browsingPortfolioConfigModalStore: {
      showConfigurePlanModal,
      dynamicForm: configDynamicForm,
      form: configForm,
      loading: configPlanModalLoading,
      portfolioBrowsingCriteria,
      saveAnswers,
      openConfigurePlanModal,
      closeConfigurePlanModal,
    },
  } = useRootStore();

  const convertedFilteredPortfolios = filteredPortfolios.map((portfolio) => toJS(portfolio)).slice();
  const convertedMarketingTags = marketingTags.map((tag) => toJS(tag)).slice();
  const convertedProductLineAttrs = productLineAttrs.map((attr) => toJS(attr)).slice();
  const convertedCart = toJS(cart);
  const convertedConfiguredPortfolioData = toJS(currentConfiguredPortfolioData);
  const convertedProductLineId = (productLineId as ProductLineId) || convertedCart.cartItems[0].productLineId;
  const lastUpdatedShortName = productLineId ? convertedCart.lastUpdateBrowsingCriteria.shortName[productLineId] : '';

  useEffect(() => {
    if (productLineId && cartId) {
      void collectInitialData({
        cartId,
        productLineId: convertedProductLineId,
        type,
        portfolioId,
        portfolioIds,
        productIds,
      });
    }
  }, [productLineId, cartId]);

  const openConfigurePortfolioModal = (
    id: string,
    portfolioName: string,
    pLineId: ProductLineId,
    bcIndex: number | null,
  ): void => {
    setConfiguredPortfolio(id, portfolioName, pLineId);
    const currentPortfolio = convertedFilteredPortfolios.find((portfolio) => portfolio.id === portfolioId);
    if (currentPortfolio) {
      void openConfigurePlanModal(currentPortfolio, convertedCart.lastUpdateBrowsingCriteria.effectiveDate, bcIndex);
    }
  };

  const savePortfolioConfigModalChanges = async (): Promise<void> => {
    closeConfigurePlanModal();
    saveAnswers();
    if (convertedConfiguredPortfolioData) {
      const { portfolioId: id, portfolioName, productLineId: pLineId } = convertedConfiguredPortfolioData;

      const fullNewVersionBC = portfolioBrowsingCriteria[pLineId];
      if (fullNewVersionBC) {
        const { bcIndex, additionalConfig } = findMatchingVersionIndex(fullNewVersionBC, convertedCart.cartItems, id);
        // The fullNewVersionBC value serves the purpose of incorporating portfolio configuration properties into the browsing criteria.
        // This ensures the precise representation of the correct version when adding to the proposal
        await onAddToProposal(id, bcIndex, portfolioName, pLineId, fullNewVersionBC, additionalConfig);
      }
    }
  };

  return (
    <div className={classes.content}>
      <>
        {loading ? (
          <FormsSpinner display={loading} />
        ) : (
          <>
            {convertedFilteredPortfolios.length > 0 && (
              <>
                <ComparisonTable
                  containerStyles={classes.tableContent}
                  lastUpdateBcShortName={lastUpdatedShortName}
                  addButtonTitle="Add To Proposal"
                  removeButtonTitle="Remove"
                  onAddToProposal={onAddToProposal}
                  onRemoveFromProposal={onRemoveFromProposal}
                  onConfigure={openConfigurePortfolioModal}
                  cartItems={convertedCart.cartItems.map((dateObj) => toJS(dateObj)).slice()}
                  portfolios={convertedFilteredPortfolios}
                  marketingTags={convertedMarketingTags}
                  productLineAttributes={convertedProductLineAttrs}
                  loading={loading}
                  productLineId={convertedProductLineId}
                  loaderInPortfolioId={loaderInPortfolioId}
                  highlightBCAndConfigurePortfolio
                />
                <DynamicFormModalDEPRECATED
                  key="configure-plan-modal"
                  show={showConfigurePlanModal}
                  onClose={(): void => {
                    closeConfigurePlanModal();
                    resetConfiguredPortfolio();
                  }}
                  onAction={savePortfolioConfigModalChanges}
                  dynamicForm={configDynamicForm}
                  form={configForm}
                  loading={configPlanModalLoading}
                  formStyles={classes.bcForm}
                  submitButtonText="Add"
                />
              </>
            )}
            <Toast show={showToast} onClose={closeToast} colorType={toastType}>
              {toastMessage}
            </Toast>
          </>
        )}
      </>
    </div>
  );
});

export default ConfigComparisonContent;
